/**
 * LocationsView — DM-facing tab for the per-campaign geography.
 *
 * Smart wrapper. The four CRUD mutations come from the shared
 * useWorldEntityMutations factory; this file adds the list fetch, the awaitable
 * handler callbacks, and the two bulk (Promise.all) helpers with their own
 * bulk-busy flag. The render surface lives in LocationsViewView/, which is
 * dumb (no useCompanyApi / useQuery / useMutation), receives the loaded data as
 * props, and emits user intents back via callbacks.
 *
 * Phase WS-6 — sibling to NPCsView. Location-specific surface: `type` enum
 * (city / region / dungeon / wilderness / structure / plane / realm),
 * `parentLocationId` for hierarchy, `status` enum (known / visited /
 * destroyed / lost / unreachable).
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  acceptLocation,
  createLocation,
  deleteLocation,
  fetchLocations,
  type ILocationCreateBody,
  type ILocationEditBody,
  updateLocation,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { queryKeys } from "../../lib/query-keys"
import { useWorldEntityMutations } from "../WorldEntityShell/useWorldEntityMutations"
import type { IProps } from "./interfaces"
import { LocationsViewView } from "./LocationsViewView"

export function LocationsView({ seriesId, initialEntityId, initialCreateName }: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const locationsQuery = useCompanyQuery({
    key: (org) => queryKeys.worldLocations(org, seriesId),
    queryFn: (client) => fetchLocations(client, seriesId),
  })

  const { createMutation, updateMutation, acceptMutation, deleteMutation } = useWorldEntityMutations<
    ILocationCreateBody,
    ILocationEditBody
  >({
    seriesId,
    queryKey: (org) => queryKeys.worldLocations(org, seriesId),
    create: createLocation,
    update: updateLocation,
    accept: acceptLocation,
    remove: deleteLocation,
    labels: {
      created: msg`Location created`,
      saved: msg`Location saved`,
      accepted: msg`Location accepted`,
      deleted: msg`Location deleted`,
    },
  })

  // Bulk accept/delete spans an unbounded number of pending entries, so we
  // keep them as ad-hoc Promise.all calls instead of pushing each through
  // the per-item mutation pipeline (which would fire N notifications).
  const [bulkBusy, setBulkBusy] = useState<boolean>(false)
  const allLocations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data])

  const handleAcceptAllPending = useCallback(async (): Promise<void> => {
    if (!company) return
    const pending = allLocations.filter((l) => l.pending)
    if (pending.length === 0) return
    setBulkBusy(true)
    try {
      await Promise.all(pending.map((l) => acceptLocation(company, seriesId, l.id)))
      notifications.show({ message: i18n._(msg`Accepted ${pending.length} locations`), color: "success" })
      queryClient.invalidateQueries({ queryKey: queryKeys.worldLocations(orgId, seriesId) })
    } catch (err) {
      notifications.show({ message: extractErrorMessage(err), color: "danger" })
    } finally {
      setBulkBusy(false)
    }
  }, [company, allLocations, seriesId, queryClient, i18n, orgId])

  const handleDeleteAllPending = useCallback(async (): Promise<void> => {
    if (!company) return
    const pending = allLocations.filter((l) => l.pending)
    if (pending.length === 0) return
    setBulkBusy(true)
    try {
      await Promise.all(pending.map((l) => deleteLocation(company, seriesId, l.id)))
      notifications.show({ message: i18n._(msg`Deleted ${pending.length} pending locations`), color: "success" })
      queryClient.invalidateQueries({ queryKey: queryKeys.worldLocations(orgId, seriesId) })
    } catch (err) {
      notifications.show({ message: extractErrorMessage(err), color: "danger" })
    } finally {
      setBulkBusy(false)
    }
  }, [company, allLocations, seriesId, queryClient, i18n, orgId])

  const handleCreate = useCallback(
    async (body: ILocationCreateBody): Promise<void> => {
      await createMutation.mutateAsync(body)
    },
    [createMutation],
  )

  const handleUpdate = useCallback(
    async (locationId: string, patch: ILocationEditBody): Promise<void> => {
      await updateMutation.mutateAsync({ id: locationId, patch })
    },
    [updateMutation],
  )

  const handleAccept = useCallback(
    async (locationId: string): Promise<void> => {
      await acceptMutation.mutateAsync(locationId)
    },
    [acceptMutation],
  )

  const handleDelete = useCallback(
    async (locationId: string): Promise<void> => {
      await deleteMutation.mutateAsync(locationId)
    },
    [deleteMutation],
  )

  return (
    <LocationsViewView
      locations={allLocations}
      isLoading={locationsQuery.isLoading}
      initialEntityId={initialEntityId}
      initialCreateName={initialCreateName}
      isCreating={createMutation.isPending}
      isUpdating={updateMutation.isPending}
      isAccepting={acceptMutation.isPending}
      isDeleting={deleteMutation.isPending}
      isBulkBusy={bulkBusy}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onAccept={handleAccept}
      onDelete={handleDelete}
      onAcceptAllPending={handleAcceptAllPending}
      onDeleteAllPending={handleDeleteAllPending}
    />
  )
}
